import { v4 as uuidv4 } from 'uuid';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { useMemo } from 'react';
import { FormSpy } from 'react-final-form';

import {
  getActKnoInspectorBlockId,
  getActKnoInspectorsBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { KnmLateFieldsRemark } from 'KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

import styles from '../../ErknmActsItem.module.scss';
import { getKnmDataCreated, getKnmFormRef } from '../../../../../KnmPackage/MainModule/selectors/knmSelectors';
import { isLaterThanAdminSetting } from '../../../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IDictionarySelectOption } from '../../../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IParticipantPosition } from '../../../../../APIPackage/DictionariesModule/interfaces/IParticipantPosition';
import { ErknmFieldMark } from '../../../../MainModule/components/ErknmFieldMark';
import { ShowMoreButton } from '../../../../../UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { useShowMore } from '../../../../../hooks/useShowMore';
import { Spinner } from '../../../../../UIPackage/MainModule/components/Spinner';

interface IErknmActsItemProps {
  id: string;
  index: number;
  inspectorsErknm: IKnmErknmFormInspector[] | undefined;
  isWatchOnly: boolean;
  name: string;
  participantPositionsSelectOptions: IDictionarySelectOption<IParticipantPosition>[] | undefined;
  isDisabledByComplete: boolean;
  lateFieldName: string;
}

interface IErknmActsItemStoreProps {
  created: string | null | undefined;
  formRef: IKnmFormRef | undefined;
  settings: IAdminSetting[] | undefined;
}

const ErknmActsItemContentKnoInspectorsWithoutStore = ({
  id,
  index,
  inspectorsErknm,
  isWatchOnly,
  name,
  participantPositionsSelectOptions,
  created,
  formRef,
  settings,
  isDisabledByComplete,
  lateFieldName,
}: IErknmActsItemProps & IErknmActsItemStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  // @ts-ignore
  const knoInspectors = formRef?.current.getFieldState(`${name}.knoInspectors`)?.value as IKnmErknmFormInspector[];
  const knoInspectorsBlockId = getActKnoInspectorsBlockId(id);

  const isShow22 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM);

  const isDisabledField = isWatchOnly || isDisabledByComplete;

  const addField = (fieldName: string) => () =>
    !isDisabledField && push && push(`${name}.${fieldName}`, { listId: uuidv4(), isNew: true });
  const makeRemoveField = (fieldName: string, i: number) => () => {
    !isDisabledField && remove && remove(`${name}.${fieldName}`, i);
  };

  /**
   * Фильтрованный список вариантов для выбора должностного лица, без ранее использованных
   */
  const inspectorsErknmOptionsFiltered = useMemo(() => {
    return (
      inspectorsErknm
        ?.filter(inspector =>
          Array.isArray(knoInspectors)
            ? !knoInspectors?.find(i => i.knoInspector?.value.listId === inspector.listId)
            : true,
        )
        .map(inspector => {
          return {
            id: inspector.guid || '',
            value: inspector,
            label: `${inspector.fullName} - ${inspector.position?.label}`,
          };
        }) || []
    );
  }, [inspectorsErknm, knoInspectors]);

  return (
    <>
      <FieldBlock
        id={knoInspectorsBlockId}
        label={
          <>
            Должностные лица контрольного (надзорного) органа,
            <br />
            участвовавшие в контрольном (надзорном) мероприятии
            <ErknmFieldMark fieldName={`${name}.knoInspectors`} />
          </>
        }
        tooltip={
          <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/knoInspectors`} fieldId={knoInspectorsBlockId} />
        }
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={isDisabledField}
            onClick={addField('knoInspectors')}
          />
        }
      >
        <FieldArray name={`${name}.knoInspectors`}>
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormInspector, HTMLElement>) => {
            const inspectors = fields.value || [];
            const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(inspectors, {
              initialCount: 10,
              incrementCount: 10,
            });

            if (!inspectors.length) {
              return null;
            }

            return (
              <>
                <Row>
                  {visibleItems.map((fieldValue, visibleIndex) => {
                    const realIndex = inspectors.findIndex(item => item === fieldValue);

                    if (realIndex === -1) return null;

                    const fieldName = `${name}.knoInspectors[${realIndex}]`;
                    const rowListId = fieldValue.listId || `idx-${realIndex >= 0 ? realIndex : visibleIndex}`;

                    if (isShow22) {
                      const knoInspectorId = `${getActKnoInspectorBlockId(id, rowListId)}.knoInspector`;

                      return (
                        <Row key={knoInspectorId} margin="medium" isListItem notSpace notWrap>
                          <SelectInputField
                            className={styles.SelectInputKnoInspectors}
                            name={`${fieldName}.knoInspector`}
                            id={knoInspectorId}
                            placeholder={
                              <>
                                Выберите должностное лицо
                                <ErknmFieldMark fieldName={`${fieldName}.knoInspector`} />
                              </>
                            }
                            options={inspectorsErknmOptionsFiltered}
                            isSearchable
                            disabled={isDisabledField}
                            size="medium"
                          />
                          <CloseButton
                            className="shared-deleting-row-btn"
                            onClick={makeRemoveField('knoInspectors', realIndex)}
                          />
                          <KnmLateFieldsRemark
                            lateFieldPath={`${lateFieldName}/knoInspectors/${fieldValue.listId}/position/title`}
                            fieldId={knoInspectorId}
                            isLeftPosition={true}
                          />
                        </Row>
                      );
                    }

                    const knoInspectorId = getActKnoInspectorBlockId(id, rowListId);

                    const isAvailablePosition = !!participantPositionsSelectOptions?.find(
                      participant => participant.id === fieldValue.position?.id,
                    );

                    return (
                      <Row key={knoInspectorId} margin="medium" isListItem notSpace>
                        <FieldBlock id={`${knoInspectorId}.fullName`} size="half">
                          <TextInputField
                            name={`${fieldName}.fullName`}
                            placeholder="Введите ФИО"
                            disabled={isDisabledField}
                          />
                          <KnmLateFieldsRemark
                            lateFieldPath={`${lateFieldName}.knoInspectors/${fieldValue.listId}/fullName`}
                            fieldId={`${knoInspectorId}.fullName`}
                            isLeftPosition={true}
                          />
                        </FieldBlock>

                        <SelectInputField
                          id={`${knoInspectorId}.position`}
                          className={styles.InspectorsSelect}
                          name={`${fieldName}.position`}
                          placeholder="Выберите должность"
                          options={participantPositionsSelectOptions}
                          size="medium"
                          disabled={
                            // eslint-disable-next-line no-nested-ternary
                            fieldValue.position ? (isAvailablePosition ? isDisabledField : false) : isDisabledField
                          }
                          isClearable={false}
                        />
                        <CloseButton
                          className="shared-deleting-row-btn"
                          onClick={makeRemoveField('knoInspectors', realIndex)}
                        />
                        <KnmLateFieldsRemark
                          lateFieldPath={`${lateFieldName}.knoInspectors/${fieldValue.listId}/fullName`}
                          fieldId={`${knoInspectorId}.position`}
                          isLeftPosition={true}
                        />
                      </Row>
                    );
                  })}
                </Row>
                {isRendering && <Spinner className={styles.Loader} />}
                {hasMore && !isRendering && (
                  <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
                )}
              </>
            );
          }}
        </FieldArray>
      </FieldBlock>
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          if (props.errors?.acts && props.errors?.acts[index]?.knoInspectors) {
            const fieldError = props.errors.acts[index].knoInspectors;
            const isFieldTouched = props.touched?.[`acts[${index}].knoInspectors`];

            const errorMessage: string | undefined = fieldError
              ? (Object.values(fieldError).reduce((prev: string, curr: string) => prev + curr, '') as string)
              : undefined;

            if (isFieldTouched && errorMessage && typeof fieldError === 'string') {
              return (
                <p className={`${styles.knoInspectorsFormSpyMessage} shared-input-error`}>Поле не может быть пустым</p>
              );
            }
          }

          return null;
        }}
      </FormSpy>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmActsItemStoreProps => ({
  created: getKnmDataCreated(state),
  formRef: getKnmFormRef(state),
  settings: getAdminFetchSettings(state),
});

export const ErknmActsItemContentKnoInspectors = connect(mapStateToProps)(
  ErknmActsItemContentKnoInspectorsWithoutStore,
);
